/**
 * Itinerary Export Controller
 *
 * Handles Excel/CSV export functionality specifically for the Days page
 * itinerary data. Provides structured export of trip schedule and activities
 * in spreadsheet formats.
 *
 * Features:
 * - Excel (.xlsx) and CSV export formats
 * - Comprehensive itinerary data structure
 * - Day-by-day schedule breakdown
 * - Integration with accommodation and travel data
 * - User-friendly column headers and formatting
 */

import { dataManagement } from '@trip-planner-monorepo/app-features';

const excelGenerator = dataManagement.formats.excel;
const csvGenerator = dataManagement.formats.csv;

// Import domain services asynchronously to get real data with graceful fallback
let daysService, accommodationService, travelService;
let servicesLoaded = false;

// Async service loading function for ES6 modules
async function loadServices() {
  try {
    const daysModule = await import('@trip-planner-monorepo/days');
    daysService = daysModule.daysService;
  } catch (error) {
    console.warn('Days service not available for itinerary export:', error.message);
  }

  try {
    const accommodationModule = await import('@trip-planner-monorepo/accommodation');
    accommodationService = accommodationModule.accommodationService;
  } catch (error) {
    console.warn('Accommodation service not available for itinerary export:', error.message);
  }

  try {
    const travelModule = await import('@trip-planner-monorepo/travel');
    travelService = travelModule.travelService;
  } catch (error) {
    console.warn('Travel service not available for itinerary export:', error.message);
  }
}

class ItineraryExportController {
  constructor() {
    this.supportedFormats = ['xlsx', 'csv'];
    this.defaultFormat = 'xlsx';
  }

  /**
   * Export itinerary in specified format
   *
   * @param {Object} context - Current application context
   * @param {string} context.tripId - Current trip ID
   * @param {Object} context.trip - Current trip data
   * @param {Object} context.user - Current user data
   * @param {string} format - Export format ('xlsx' or 'csv')
   * @returns {Promise<boolean>} Success status
   */
  async exportItinerary(context, format = this.defaultFormat) {
    try {
      if (!this.supportedFormats.includes(format)) {
        throw new Error(`Unsupported format: ${format}`);
      }

      console.log(`Exporting itinerary in ${format.toUpperCase()} format...`);

      // Show user feedback
      this.showExportStartedNotification(format);

      // Get itinerary data
      const result = await this.prepareItineraryData(context);

      if (!result || !result.itineraryData || result.itineraryData.length === 0) {
        throw new Error('No itinerary data found for this trip');
      }

      // Generate and download file
      if (format === 'xlsx') {
        await this.generateExcelFile(result.itineraryData, result.metadata);
      } else {
        await this.generateCSVFile(result.itineraryData, result.metadata);
      }

      this.showExportCompletedNotification(format);
      return true;
    } catch (error) {
      console.error('Itinerary Export failed:', error);
      this.showExportErrorNotification(error.message);
      return false;
    }
  }

  /**
   * Prepare structured itinerary data for export
   *
   * @param {Object} context - Current application context
   * @returns {Promise<Array>} Structured itinerary data
   */
  async prepareItineraryData(context) {
    try {
      // Ensure services are loaded before attempting to use them
      if (!servicesLoaded) {
        await loadServices();
        servicesLoaded = true;
      }

      // Validate that daysService is available (required for itinerary export)
      if (!daysService) {
        throw new Error('Days service is not available. Cannot export itinerary without day data.');
      }

      // Fetch real Firebase data
      console.log('[Itinerary Export] Fetching days data from Firebase...');
      const days = await daysService.fetchDays(context.user.uid, context.tripId);

      // Validate that we have days to export
      if (!days || days.length === 0) {
        throw new Error('No days found for this trip. Please add days to your itinerary before exporting.');
      }

      console.log(`[Itinerary Export] Found ${days.length} days`);

      // Fetch accommodation and travel data (optional - gracefully handle if unavailable)
      let accommodationData = [];
      let travelData = [];

      if (accommodationService) {
        try {
          accommodationData = await accommodationService.fetchAccommodation(context.user.uid, context.tripId);
          console.log(`[Itinerary Export] Found ${accommodationData?.length || 0} accommodation items`);
        } catch (error) {
          console.warn('[Itinerary Export] Could not fetch accommodation data:', error);
        }
      }

      if (travelService) {
        try {
          travelData = await travelService.fetchTravel(context.user.uid, context.tripId);
          console.log(`[Itinerary Export] Found ${travelData?.length || 0} travel items`);
        } catch (error) {
          console.warn('[Itinerary Export] Could not fetch travel data:', error);
        }
      }

      // Transform the data to itinerary format
      const itineraryData = this.transformDaysToItinerary(days, accommodationData, travelData, context);

      // Calculate trip metadata (date range and day count)
      const tripMetadata = this.calculateTripMetadata(days, context);

      // Return both itinerary data and metadata
      return {
        itineraryData,
        metadata: tripMetadata
      };
    } catch (error) {
      console.error('[Itinerary Export] Error preparing itinerary data:', error);
      throw error;
    }
  }

  /**
   * Calculate trip metadata (date range and day count)
   *
   * @param {Array} days - Days data
   * @param {Object} context - Application context
   * @returns {Object} Trip metadata
   */
  calculateTripMetadata(days, context) {
    if (!days || days.length === 0) {
      return {
        tripName: context.trip?.name || 'Trip',
        dateRange: '',
        dayCount: 0
      };
    }

    // Sort days by date
    const sortedDays = [...days].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    // Get first and last day
    const firstDay = sortedDays[0];
    const lastDay = sortedDays[sortedDays.length - 1];

    // Format date range as "Mon, 1 Sep 2025 - Fri, 5 Sep 2025"
    let dateRange = '';
    if (firstDay.date && lastDay.date) {
      try {
        const firstDate = new Date(firstDay.date);
        const lastDate = new Date(lastDay.date);

        const formatDate = (date) => date.toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });

        dateRange = `${formatDate(firstDate)} - ${formatDate(lastDate)}`;
      } catch (error) {
        console.warn('[Itinerary Export] Error formatting date range:', error);
      }
    }

    return {
      tripName: context.trip?.name || 'Trip',
      dateRange,
      dayCount: days.length
    };
  }

  /**
   * Transform days data to itinerary export format
   *
   * @param {Array} days - Days data from Firebase
   * @param {Array} accommodationData - Accommodation data
   * @param {Array} travelData - Travel data
   * @param {Object} context - Application context
   * @returns {Array} Formatted itinerary data
   */
  transformDaysToItinerary(days, accommodationData, _travelData, _context) {
    // Sort days by date
    const sortedDays = [...days].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    // Map each day to itinerary format (simplified for user requirements)
    return sortedDays.map((day, index) => {
      const dayNumber = index + 1;

      // Format date as "Mon, 1 Sep 2025"
      let formattedDate = '';
      if (day.date) {
        try {
          const dateObj = new Date(day.date);
          formattedDate = dateObj.toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
        } catch (error) {
          console.warn(`[Itinerary Export] Error formatting date for day ${dayNumber}:`, error);
        }
      }

      // Extract activities from contentBlocks
      const activities = this.extractActivities(day);

      // Extract accommodation details for this day
      const accommodation = this.extractAccommodationDetails(day, accommodationData);

      return {
        dayNumber,
        date: formattedDate,
        activities,
        accommodation
      };
    });
  }

  /**
   * Extract accommodation details for a specific day
   * Format matches accommodation detail PDF: Name, Address, Phone, Website
   *
   * @param {Object} day - Day data
   * @param {Array} accommodationData - All accommodation data
   * @returns {string} Formatted accommodation details
   */
  extractAccommodationDetails(day, accommodationData) {
    if (!accommodationData || accommodationData.length === 0 || !day.content) {
      return '';
    }

    // Find accommodation reference in day.content (same structure as extractActivities)
    const accommodationBlock = day.content.find(
      block => block.type === 'accommodation_reference' && block.accommodationId
    );

    if (!accommodationBlock) {
      return '';
    }

    // Find the actual accommodation data
    const accommodation = accommodationData.find(
      acc => acc.id === accommodationBlock.accommodationId
    );

    if (!accommodation) {
      return '';
    }

    // Extract accommodation details (matching PDF format)
    const details = accommodation.details || {};
    const parts = [];

    // Name (from details or summary)
    const name = details.name || accommodation.summary || 'Accommodation';
    parts.push(name);

    // Address
    if (details.address) {
      parts.push(details.address);
    }

    // Phone - extract from details object
    if (details.phone) {
      parts.push(`Phone: ${details.phone}`);
    }

    // Website - check both url and website fields
    const website = details.url || details.website;
    if (website) {
      parts.push(`Website: ${website}`);
    }

    // Join with newlines for multi-line display in Excel
    return parts.join('\n');
  }

  /**
   * Extract activities from day's content - matches ContentBlockRenderer display logic
   *
   * @param {Object} day - Day data
   * @returns {string} Activities as formatted string matching Days list display
   */
  extractActivities(day) {
    console.log('[extractActivities] Processing day:', {
      id: day.id,
      hasContent: !!day.content,
      contentType: Array.isArray(day.content) ? 'array' : typeof day.content,
      contentLength: Array.isArray(day.content) ? day.content.length : 'N/A',
      hasContentBlocks: !!day.contentBlocks,
      hasTitle: !!day.title
    });

    // Check if day.content exists (structured format used by ContentBlockRenderer)
    if (day.content && Array.isArray(day.content)) {
      const contentParts = [];

      day.content.forEach((block, index) => {
        console.log(`[extractActivities] Processing block ${index}:`, {
          type: block.type,
          hasText: !!block.text,
          hasDisplayText: !!block.displayText,
          text: block.text?.substring(0, 50),
          displayText: block.displayText?.substring(0, 50)
        });

        if (block.type === 'text' && block.text) {
          // Text content blocks - main activity text
          contentParts.push(block.text);
        } else if (block.type === 'accommodation_reference' && block.displayText) {
          // Accommodation with emoji matching ContentBlockRenderer
          // Format: "Stay at Hotel Name: City, Country"
          contentParts.push(`🏨 ${block.displayText}`);
        } else if (block.type === 'travel_reference' && block.displayText) {
          // Travel information
          contentParts.push(block.displayText);
        } else if (block.type === 'place_reference' && block.displayText) {
          // Place references
          contentParts.push(block.displayText);
        }
      });

      const result = contentParts.length > 0 ? contentParts.join('\n') : (day.title || '');
      console.log('[extractActivities] Result:', {
        partsCount: contentParts.length,
        resultLength: result.length,
        preview: result.substring(0, 100)
      });

      return result;
    }

    // Fallback for legacy format or missing content
    console.log('[extractActivities] Using fallback - day.title:', day.title);
    return day.title || '';
  }

  /**
   * Find accommodation related to this day
   *
   * @param {Object} day - Day data
   * @param {Array} accommodationData - All accommodation data
   * @returns {string} Accommodation description
   */
  findRelatedAccommodation(day, accommodationData) {
    if (!accommodationData || accommodationData.length === 0) {
      return '';
    }

    // Check for accommodation references in contentBlocks
    if (day.contentBlocks && Array.isArray(day.contentBlocks)) {
      const accommodationBlock = day.contentBlocks.find(
        block => block.type === 'accommodation_reference' && block.relatedEntityId
      );

      if (accommodationBlock) {
        const accommodation = accommodationData.find(
          acc => acc.id === accommodationBlock.relatedEntityId
        );

        if (accommodation) {
          const context = accommodationBlock.context || '';
          const name = accommodation.details?.name || accommodation.summary || 'Accommodation';
          return context ? `${name} (${context})` : name;
        }
      }
    }

    // Fallback: Check if day date is within accommodation date range
    if (day.date) {
      const dayDate = new Date(day.date);

      for (const accommodation of accommodationData) {
        const checkIn = accommodation.details?.checkInDate ? new Date(accommodation.details.checkInDate) : null;
        const checkOut = accommodation.details?.checkOutDate ? new Date(accommodation.details.checkOutDate) : null;

        if (checkIn && checkOut && dayDate >= checkIn && dayDate <= checkOut) {
          return accommodation.details?.name || accommodation.summary || 'Accommodation';
        }
      }
    }

    return '';
  }

  /**
   * Find travel related to this day
   *
   * @param {Object} day - Day data
   * @param {Array} travelData - All travel data
   * @returns {string} Travel description
   */
  findRelatedTravel(day, travelData) {
    if (!travelData || travelData.length === 0) {
      return '';
    }

    // Check for travel references in contentBlocks
    if (day.contentBlocks && Array.isArray(day.contentBlocks)) {
      const travelBlock = day.contentBlocks.find(
        block => block.type === 'travel_reference' && block.relatedEntityId
      );

      if (travelBlock) {
        const travel = travelData.find(t => t.id === travelBlock.relatedEntityId);
        if (travel) {
          return this.formatTravel(travel);
        }
      }
    }

    // Fallback: Check if travel departure or arrival date matches this day
    if (day.date) {
      const dayDate = new Date(day.date).toISOString().split('T')[0];

      for (const travel of travelData) {
        const departureDate = travel.details?.departureTime ?
          new Date(travel.details.departureTime).toISOString().split('T')[0] : null;
        const arrivalDate = travel.details?.arrivalTime ?
          new Date(travel.details.arrivalTime).toISOString().split('T')[0] : null;

        if (departureDate === dayDate || arrivalDate === dayDate) {
          return this.formatTravel(travel);
        }
      }
    }

    return '';
  }

  /**
   * Extract location from day or accommodation
   *
   * @param {Object} day - Day data
   * @param {Array} accommodationData - All accommodation data
   * @returns {string} Location description
   */
  extractLocation(day, accommodationData) {
    // First try accommodation city for this day
    if (day.contentBlocks && Array.isArray(day.contentBlocks)) {
      const accommodationBlock = day.contentBlocks.find(
        block => block.type === 'accommodation_reference' && block.relatedEntityId
      );

      if (accommodationBlock) {
        const accommodation = accommodationData.find(
          acc => acc.id === accommodationBlock.relatedEntityId
        );

        if (accommodation?.details?.city) {
          const city = accommodation.details.city;
          const country = accommodation.details.country;
          return country ? `${city}, ${country}` : city;
        }
      }
    }

    // Fallback to day location or title
    return day.location || day.title || '';
  }

  /**
   * Format travel information for display
   *
   * @param {Object} travel - Travel data
   * @returns {string} Formatted travel string
   */
  formatTravel(travel) {
    if (!travel) return '';

    const type = travel.type || 'Travel';
    const details = travel.details || {};

    if (type === 'flight') {
      const airline = details.airline || '';
      const flightNumber = details.flightNumber || '';
      const time = details.departureTime ?
        new Date(details.departureTime).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        }) : '';

      return `Flight ${airline} ${flightNumber}${time ? ' at ' + time : ''}`.trim();
    } else if (type === 'train') {
      const operator = details.operator || '';
      const trainNumber = details.trainNumber || '';
      return `Train ${operator} ${trainNumber}`.trim();
    } else if (type === 'bus') {
      const operator = details.operator || '';
      return `Bus ${operator}`.trim();
    }

    return travel.summary || type;
  }

  /**
   * Extract meals from day's contentBlocks
   *
   * @param {Object} day - Day data
   * @returns {string} Meals as formatted string
   */
  extractMeals(day) {
    if (!day.contentBlocks || !Array.isArray(day.contentBlocks)) {
      return '';
    }

    const mealBlocks = day.contentBlocks
      .filter(block => block.type === 'meal')
      .map(block => block.content || block.text || '')
      .filter(content => content.trim().length > 0);

    return mealBlocks.join(', ');
  }

  /**
   * Extract budget from day's contentBlocks
   *
   * @param {Object} day - Day data
   * @returns {string} Budget information
   */
  extractBudget(day) {
    if (!day.contentBlocks || !Array.isArray(day.contentBlocks)) {
      return '';
    }

    const budgetBlock = day.contentBlocks.find(block => block.type === 'budget');

    if (budgetBlock) {
      const amount = budgetBlock.amount || budgetBlock.content || '';
      const currency = budgetBlock.currency || '';
      return currency ? `${currency}${amount}` : amount;
    }

    return '';
  }

  /**
   * Generate Excel file from itinerary data
   *
   * @param {Array} data - Itinerary data
   * @param {Object} metadata - Trip metadata (name, dateRange, dayCount)
   */
  async generateExcelFile(data, metadata) {
    console.log('Generating Excel file using ExcelJS...');

    try {
      // Use the proper ExcelJS generator from data-management package
      const blob = await excelGenerator.generateItineraryExcel(data, metadata);
      const filename = this.generateFilename(metadata);

      // Download the Excel blob
      excelGenerator.downloadExcelFile(blob, filename);
    } catch (error) {
      console.error('Excel generation failed:', error);
      throw new Error('Failed to generate Excel file: ' + error.message);
    }
  }

  /**
   * Generate CSV file from itinerary data
   *
   * @param {Array} data - Itinerary data
   * @param {Object} metadata - Trip metadata (name, dateRange, dayCount)
   */
  async generateCSVFile(data, metadata) {
    console.log('Generating CSV file using CSV generator...');

    try {
      // Use the proper CSV generator from data-management package
      const csvContent = csvGenerator.generateItineraryCsv(data);
      const filename = `${this.generateFilename(metadata)}.csv`;

      // Download the CSV content
      this.downloadFile(csvContent, filename, 'text/csv');
    } catch (error) {
      console.error('CSV generation failed:', error);
      throw new Error('Failed to generate CSV file: ' + error.message);
    }
  }

  /**
   * Generate appropriate filename based on trip metadata
   *
   * @param {Object} metadata - Trip metadata (name, dateRange, dayCount)
   * @returns {string} Generated filename
   */
  generateFilename(metadata) {
    const tripName = (metadata?.tripName || 'trip').replace(/[^a-zA-Z0-9]/g, '-');
    const date = new Date().toISOString().split('T')[0];
    return `${tripName}-itinerary-${date}`;
  }

  /**
   * Download file with specified content and type
   *
   * @param {string} content - File content
   * @param {string} filename - File name
   * @param {string} mimeType - MIME type
   */
  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Check if itinerary export is available
   *
   * @param {Object} context - Current application context
   * @returns {boolean} Whether export is available
   */
  isExportAvailable(context) {
    return !!(context.trip && context.tripId && context.user);
  }

  /**
   * Check if user is on the days page (required for itinerary export)
   *
   * @param {string} pathname - Current route pathname
   * @returns {boolean} Whether user is on days page
   */
  isOnDaysPage(pathname) {
    return pathname.includes('/trips/') &&
           !pathname.includes('/places') &&
           !pathname.includes('/travel') &&
           !pathname.includes('/accommodation') &&
           !pathname.includes('/documents') &&
           !pathname.includes('/extras');
  }

  /**
   * Get export options for the user to choose from
   *
   * @returns {Array} Available export options
   */
  getExportOptions() {
    return [
      {
        format: 'xlsx',
        label: 'Excel Spreadsheet (.xlsx)',
        description: 'Full-featured spreadsheet with formatting',
        icon: 'excel'
      },
      {
        format: 'csv',
        label: 'CSV File (.csv)',
        description: 'Comma-separated values for universal compatibility',
        icon: 'csv'
      }
    ];
  }

  // Notification methods (to be integrated with actual notification system)

  showExportStartedNotification(format) {
    console.log(`Starting itinerary export in ${format.toUpperCase()} format...`);
  }

  showExportCompletedNotification(format) {
    console.log(`Itinerary export completed successfully as ${format.toUpperCase()}`);
  }

  showExportErrorNotification(errorMessage) {
    console.error('Itinerary export error:', errorMessage);
  }

  /**
   * Show itinerary export modal with format selection
   *
   * @param {Object} context - Current application context
   * @returns {Promise<void>} Modal display (async)
   */
  async showItineraryExportModal(context) {
    try {
      // Import the modal component dynamically
      const { ItineraryExportModal } = await import('@trip-planner-monorepo/app-features');

      // Create and show modal
      return this.renderModal(ItineraryExportModal, context);
    } catch (error) {
      console.error('Failed to load itinerary export modal:', error);
      // Fallback to direct export with default format
      return await this.exportItinerary(context, 'xlsx');
    }
  }

  /**
   * Render modal component (to be implemented by framework integration)
   *
   * @param {Component} ModalComponent - React modal component
   * @param {Object} context - Application context
   * @returns {Promise<void>} Modal rendering promise
   */
  async renderModal(ModalComponent, context) {
    // This method should be implemented by the framework integration
    // For now, log the request
    console.log('Rendering ItineraryExportModal with context:', {
      tripId: context.tripId,
      tripName: context.trip?.name,
      hasData: !!(context.trip && context.user)
    });

    // In a real integration, this would:
    // 1. Create a modal instance
    // 2. Pass context as props
    // 3. Handle modal events (export, close)
    // 4. Return a promise that resolves when modal closes

    // For now, fallback to direct export
    return await this.exportItinerary(context, 'xlsx');
  }
}

// Export singleton instance
export const itineraryExportController = new ItineraryExportController();
export default itineraryExportController;